import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip as RTooltip,
} from 'recharts';
import { FAULT_COLORS, pctOf, RECENT } from './format';

const FaultBar = ({ fb, label }) => {
    if (fb.total === 0) {
        return (
            <Box sx={{ p: 1.5, background: '#f0faf7', borderRadius: 1.5, textAlign: 'center', mb: 1.5 }}>
                <b style={{ color: '#2a9d8f' }}>No failed runs</b> in {label}.
            </Box>
        );
    }
    const cell = (k) =>
        fb[k] > 0 ? (
            <div
                key={k}
                style={{
                    flex: `0 0 ${(fb[k] / fb.total * 100).toFixed(2)}%`,
                    background: FAULT_COLORS[k],
                }}
            >
                {k}: {fb[k]} ({pctOf(fb[k], fb.total)})
            </div>
        ) : null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: '#666', mb: 0.5 }}>
                {label} — <b>{fb.total}</b> failed runs
            </Typography>
            <Box className="sh-stack-bar">
                {cell('ours')}
                {cell('shared')}
                {cell('unknown')}
                {cell('theirs')}
            </Box>
        </Box>
    );
};

const FaultLegend = () => (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box sx={{ flex: '1 1 200px', p: 1.5, background: '#fdf2ee', borderLeft: '3px solid #e76f51', borderRadius: 1, fontSize: 13 }}>
            <b style={{ color: '#e76f51' }}>Ours</b> — code / infra we own.
        </Box>
        <Box sx={{ flex: '1 1 200px', p: 1.5, background: '#fdf5ea', borderLeft: '3px solid #f4a261', borderRadius: 1, fontSize: 13 }}>
            <b style={{ color: '#f4a261' }}>Theirs</b> — external (auth, 5xx, missing files).
        </Box>
        <Box sx={{ flex: '1 1 200px', p: 1.5, background: '#fef9e7', borderLeft: '3px solid #e9a23b', borderRadius: 1, fontSize: 13 }}>
            <b style={{ color: '#e9a23b' }}>Shared</b> — ambiguous (bad_request, blob_fetch).
        </Box>
        <Box sx={{ flex: '1 1 200px', p: 1.5, background: '#f4f4f4', borderLeft: '3px solid #6c757d', borderRadius: 1, fontSize: 13 }}>
            <b style={{ color: '#6c757d' }}>Unknown</b> — investigation backlog.
        </Box>
    </Stack>
);

// Per-client failure mix as a horizontal stacked bar chart. Replaces the
// 4-column table version. Sorted by total failures; top 10 only.
const PerClientMixChart = ({ data, mix }) => {
    const rows = Object.entries(mix)
        .filter(([, b]) => b.total > 0)
        .sort(([, a], [, b]) => b.total - a.total)
        .slice(0, 10)
        .map(([ci, b]) => ({
            name: data.clients[+ci],
            ours: b.ours,
            shared: b.shared,
            unknown: b.unknown,
            theirs: b.theirs,
            total: b.total,
        }));

    if (!rows.length) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                Per-client failure mix (full span)
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#888', mb: 1 }}>
                Top 10 clients by failed-run count. Bar segments = ours / shared / unknown / theirs.
            </Typography>
            <Box sx={{ width: '100%', height: Math.max(200, rows.length * 28 + 40) }}>
                <ResponsiveContainer>
                    <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#666' }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#444' }} width={120} />
                        <RTooltip
                            contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                            formatter={(v, name) => [v, name]}
                        />
                        <Bar dataKey="ours" stackId="f" fill={FAULT_COLORS.ours} />
                        <Bar dataKey="shared" stackId="f" fill={FAULT_COLORS.shared} />
                        <Bar dataKey="unknown" stackId="f" fill={FAULT_COLORS.unknown} />
                        <Bar dataKey="theirs" stackId="f" fill={FAULT_COLORS.theirs} />
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

// Top-error phases as a horizontal bar chart. Each bar coloured by fault
// bucket, sample message + example client surfaced via tooltip.
const TopErrorsChart = ({ data }) => {
    if (!data.topErrors || !data.topErrors.length) {
        return <Typography sx={{ color: '#888', fontSize: 13 }}>No error events recorded.</Typography>;
    }
    // Truncate to top 12 to keep the chart compact.
    const rows = data.topErrors.slice(0, 12).map((e) => ({
        phase: e.phase || '—',
        count: e.count,
        fault: e.fault,
        category: e.category,
        exampleClient: e.exampleClient,
        sampleMessage: (e.sampleMessage || '').slice(0, 200),
    }));

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null;
        const r = payload[0].payload;
        return (
            <Box sx={{ background: '#fff', border: '1px solid #ddd', p: 1, fontSize: 11, maxWidth: 360 }}>
                <Box sx={{ fontWeight: 600 }}>{r.phase}</Box>
                <Box sx={{ color: FAULT_COLORS[r.fault] || '#666', fontWeight: 600 }}>
                    {r.fault} / {r.category}
                </Box>
                <Box>{r.count.toLocaleString()} events</Box>
                {r.exampleClient && <Box sx={{ mt: 0.5 }}><b>{r.exampleClient}:</b></Box>}
                {r.sampleMessage && <Box sx={{ color: '#555', fontStyle: 'italic' }}>{r.sampleMessage}</Box>}
            </Box>
        );
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                Top error patterns (full span)
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#888', mb: 1 }}>
                Top 12 phases by event count. Bar colour = fault bucket. Hover for sample message + example client.
            </Typography>
            <Box sx={{ width: '100%', height: Math.max(200, rows.length * 24 + 40) }}>
                <ResponsiveContainer>
                    <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#666' }} />
                        <YAxis type="category" dataKey="phase" tick={{ fontSize: 11, fill: '#444' }} width={160} />
                        <RTooltip content={renderTooltip} />
                        <Bar dataKey="count">
                            {rows.map((r, i) => <Cell key={i} fill={FAULT_COLORS[r.fault] || '#6c757d'} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5 }}>
                Event counts include retries. Bad-request events &gt; 10/run trigger a retry-loop upgrade to "ours".
            </Typography>
        </Box>
    );
};

const FaultBreakdownSection = ({ data, agg }) => (
    <Box className="sh-card">
        <Typography className="sync-health-section-title" sx={{ mb: 1 }}>Fault attribution</Typography>
        <FaultLegend />
        <FaultBar fb={agg.fb14} label={`Last ${RECENT} days`} />
        <FaultBar fb={agg.fbFull} label="Full span" />
        <PerClientMixChart data={data} mix={agg.faultMix} />
        <TopErrorsChart data={data} />
    </Box>
);

export default FaultBreakdownSection;
